'use client';

import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { useEffect, useState } from 'react';
import { getGladlyUserIdentityJwt } from '@src/actions/faq.actions';
import { configPublic } from '@src/config.public';
import { useSession } from '@src/hooks/useSession';
import { useLocale } from '@src/i18n/LocaleProvider';
import { log } from '@src/utils/log.util';
import { instrumentAction } from "@src/utils/instrumentation";
export const GladlyChat = () => {
  const {
    locale
  } = useLocale();
  const pathname = usePathname();
  const {
    user
  } = useSession();
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isGladlyInitialized, setIsGladlyInitialized] = useState(false);
  const appId = configPublic.gladly.appId[locale.code];
  const mode = configPublic.general.env === 'prod' ? 'PROD' : 'STAGING';
  useEffect(() => {
    if (!isScriptLoaded || !window.Gladly || !appId) return;

    // Initialize Gladly Chat
    window.Gladly.init({
      appId,
      autoShowButton: true
    }).then(() => {
      log.info('Gladly Chat initialized successfully');
      setIsGladlyInitialized(true);
    }).catch((error: Error) => {
      log.error('Failed to initialize Gladly Chat:', error);
    });
  }, [isScriptLoaded, appId]);

  // Hide Gladly Chat when the user navigates away from the FAQ pages
  useEffect(() => {
    if (!isGladlyInitialized || !window.Gladly) return;

    // Communicate navigation event to Gladly
    window.Gladly?.navigate();
    if (!pathname.includes('/faq')) {
      document.querySelector('[id="gladlyChat_container"]')?.classList.add('!hidden');
      window.Gladly?.close();
    } else {
      document.querySelector('[id="gladlyChat_container"]')?.classList.remove('!hidden');
    }
  }, [pathname, isGladlyInitialized]);

  // Sync user in Gladly
  useEffect(() => {
    if (!isGladlyInitialized || !window.Gladly) return;
    try {
      if (user && !window.Gladly.getUser()) {
        window.Gladly.setUser({
          name: `${user.firstName} ${user.lastName}`,
          jwt: async () => {
            return (await instrumentAction("FaqActions.getGladlyUserIdentityJwt", getGladlyUserIdentityJwt)()) ?? null;
          }
        });
      } else if (!user && window.Gladly.getUser()) {
        window.Gladly.clearUser();
      }
    } catch (error_0) {
      log.warn('Failed to sync user in Gladly:', error_0 as Error);
    }
  }, [isGladlyInitialized, user]);

  // Don't render if appId is not configured for this locale
  if (!appId) {
    return null;
  }
  return <>
      <Script id="gladly-script" strategy="afterInteractive" onReady={() => {
      setIsScriptLoaded(true);
    }} onError={e => {
      log.error('Failed to load Gladly Chat script:', e);
    }} dangerouslySetInnerHTML={{
      __html: `
!function(c,n,r,t){if(!c[r]){var i,d,p=[];d="${mode}"!=="PROD"&&"${mode}"?"STAGING"==="${mode}"?"https://cdn.gladly.qa/gladly/chat-sdk/widget.js":"${mode}":"https://cdn.gladly.com/chat-sdk/widget.js",c[r]={init:function(){i=arguments;var e={then:function(t){return p.push({type:"t",next:t}),e},catch:function(t){return p.push({type:"c",next:t}),e}};return e}},c.__onHelpAppHostReady__=function(t){if(delete c.__onHelpAppHostReady__,(c[r]=t).loaderCdn=d,i)for(var e=t.init.apply(t,i),n=0;n<p.length;n++){var a=p[n];e="t"===a.type?e.then(a.next):e.catch(a.next)}},function(){try{var t=n.getElementsByTagName("script")[0],e=n.createElement("script");e.async=!0,e.src=d+"?q="+(new Date).getTime(),t.parentNode.insertBefore(e,t)}catch(t){}}()}}(window,document,'Gladly','${mode}');
          `.trim()
    }} />
    </>;
};